// JOBE-0161.1: Extracted AgentDetail component with W2 identity head.
// JOBE-0161.2: Quality strip — 4 KPI tiles with absolute-mode Spark, behind mock flag.
// JOBE-0161.3: Outcomes table — recent task outcomes, mock-only v1.
// JOBE-0161.4: Capability table — per-agent capabilities cross-linked to ApplicationDetail.
// JOBE-0161.5: PromptOps chip drill context + DrillContextBreadcrumb return loop.
// JOBE-0165.1: IdentityHead extracted as a named export so W5 PromptOps can reuse it.
//              Tone helpers moved to lib/kpiTone.js for the same reason.
// Extracted from AgentsPage.jsx; new /agent/:name top-level route.

import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AgentActivityRollup } from '../AgentActivityRollup.jsx';
import { parseCapabilityURN } from '../parseCapabilityURN.js';
import { withDrillContext, useDrillContext, clearDrillContext } from '../lib/drillContext.js';
import { StatusPill } from '../components/StatusPill/StatusPill.jsx';
import { PillChip } from '../components/PillChip/PillChip.jsx';
import { KpiTile } from '../components/KpiTile/KpiTile.jsx';
import { Spark } from '../components/Spark/Spark.jsx';
import { flaggedFetch } from '../lib/flags.js';
import { useDocumentTitle } from '../lib/useDocumentTitle.js';
import { useFlagStore } from '../lib/useFlagStore.js';
import { toneForRate, toneForDeviation } from '../lib/kpiTone.js';
import '../mock-wcc.js'; // registers fixtures with FIXTURES map
import './AgentDetail.css';

const POLL_INTERVAL_MS = 5000;

// ---- Helpers ----

// formatTime converts an RFC3339 string to a locale date+time string.
export function formatTime(t) {
  if (!t) return '—';
  try {
    return new Date(t).toLocaleString();
  } catch (_e) {
    return t;
  }
}

// dash renders a muted em-dash for empty / unknown values.
function dash() {
  return <span className="muted">—</span>;
}

// safeStr returns v only when it is a non-empty string.
function safeStr(v) {
  return typeof v === 'string' && v ? v : null;
}

// urnSlug returns the last path segment of a URN.
function urnSlug(u) {
  if (!u || typeof u !== 'string') return '';
  const idx = u.lastIndexOf(':');
  return idx >= 0 ? u.slice(idx + 1) : u;
}

// stringifyURN handles the two shapes the operator may serialize URNs as.
function stringifyURN(u) {
  if (u == null) return '';
  if (typeof u === 'string') return u;
  if (typeof u === 'object') {
    if (Array.isArray(u.Path) && u.Path.length > 0) {
      const kind = u.Kind || u.Authority || '';
      return `urn:jobe:${kind}:${u.Path.join(':')}`;
    }
    if (u.URN) return stringifyURN(u.URN);
  }
  return String(u);
}

// toTitleCase title-cases a string per D12.
function toTitleCase(str) {
  if (!str) return '';
  return str.replace(/(\S+)/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

// phaseTone maps agent phase to StatusPill tone.
function phaseTone(phase) {
  switch ((phase || '').toLowerCase()) {
    case 'running':
    case 'available':
      return 'ok';
    case 'pending':
    case 'degraded':
      return 'warn';
    case 'failed':
    case 'disabled':
      return 'bad';
    default:
      return 'pencil';
  }
}

// avatarColor returns a deterministic hsl color from the agent name.
function avatarColor(name) {
  if (!name) return 'hsl(210, 40%, 45%)';
  const hue = (name.charCodeAt(0) * 37) % 360;
  return `hsl(${hue}, 40%, 45%)`;
}

// Tone helpers moved to ../lib/kpiTone.js (JOBE-0165.1) so W5 can share them.

// ---- Outcomes helpers (JOBE-0161.3) ----

// formatDuration formats a millisecond duration as a human string.
// < 60s  → "Ns"
// < 3600s → "Nm Ns"
// >= 3600s → "Nh Nm"
function formatDuration(ms) {
  if (!ms && ms !== 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const rm = m % 60;
  return rm > 0 ? `${h}h ${rm}m` : `${h}h`;
}

// outcomeStatusTone maps outcome status to StatusPill tone + label.
function outcomeStatusTone(status) {
  switch ((status || '').toLowerCase()) {
    case 'ok':
    case 'done':
    case 'completed':
      return { tone: 'ok', label: 'OK' };
    case 'warn':
    case 'partial':
      return { tone: 'warn', label: 'WARN' };
    case 'bad':
    case 'failed':
    case 'error':
      return { tone: 'bad', label: 'FAILED' };
    case 'running':
    case 'pending':
    default:
      return { tone: 'pencil', label: 'PENDING' };
  }
}

// usePolledJSON polls `path` every POLL_INTERVAL_MS, returning the most recent body.
function usePolledJSON(path) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) return undefined;
    const ctrl = new AbortController();
    let cancelled = false;
    let timerId = null;
    const schedule = () => {
      if (cancelled) return;
      timerId = setTimeout(run, POLL_INTERVAL_MS);
    };
    const run = () => {
      if (cancelled) return;
      fetch(path, { credentials: 'same-origin', signal: ctrl.signal })
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`);
          return r.json();
        })
        .then((j) => {
          if (cancelled) return;
          setData(j);
          setError(null);
          schedule();
        })
        .catch((err) => {
          if (cancelled || err.name === 'AbortError') return;
          setError(err.message);
          schedule();
        });
    };
    run();
    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timerId);
    };
  }, [path]);

  return { data, error, loading: data === null };
}

// ---- TABS ----

// JOBE-0161.4: Tab order: Outcomes · Activity · Capabilities.
const TABS = ['outcomes', 'activity', 'capabilities'];

// ---- IdentityHead (JOBE-0165.1) ----
//
// Extracted from AgentDetail so the W5 PromptOps Agent page can render the
// same identity-head composition. The component accepts an `agent` object
// (the polled /api/agents/:name body, plus optional `nameSlug` to drive the
// avatar color/initial when DisplayName is missing) and a `children` slot
// for trailing chips (W2 renders a PromptOps chip; W5 renders none).
//
// agent shape (only the fields actually consumed):
//   {
//     DisplayName?: string,         // title-cased; falls back to nameSlug
//     Roles?: (string|URN)[],
//     Department?: string|URN,
//     Owner?: string|URN,
//     Phase?: string,               // drives StatusPill tone
//     nameSlug?: string,            // lowercase slug for avatar color/initial
//   }
export function IdentityHead({ agent, children }) {
  const nameSlug = (agent && agent.nameSlug) || '';
  const displayName = agent && safeStr(agent.DisplayName)
    ? toTitleCase(agent.DisplayName)
    : toTitleCase(nameSlug);

  const roleSlug = agent && Array.isArray(agent.Roles) && agent.Roles.length > 0
    ? urnSlug(stringifyURN(agent.Roles[0])) : '';
  const deptSlug = agent ? urnSlug(stringifyURN(agent.Department)) : '';
  const ownerSlug = agent && agent.Owner ? urnSlug(stringifyURN(agent.Owner)) : '';

  return (
    <div className="agent-detail-identity-head">
      <div className="agent-avatar" style={{ backgroundColor: avatarColor(nameSlug) }}>
        {(nameSlug || '').charAt(0).toUpperCase()}
      </div>

      <div className="agent-identity-info">
        <div className="agent-identity-name">{displayName}</div>
        {(roleSlug || deptSlug) && (
          <div className="agent-identity-sub">
            {[roleSlug, deptSlug].filter(Boolean).join(' · ')}
          </div>
        )}
        {ownerSlug && (
          <div className="agent-identity-owner">
            <PillChip label={ownerSlug} />
          </div>
        )}
      </div>

      <div className="agent-identity-pills">
        {agent && safeStr(agent.Phase) && (
          <StatusPill tone={phaseTone(agent.Phase)} label={agent.Phase} />
        )}
        {children}
      </div>
    </div>
  );
}

// ---- DrillContextBreadcrumb (JOBE-0161.5) ----

// Renders the "← Back to Command Center · {summary}" bar when W2 was
// reached from W1 via a drill-context fragment. Only shows when ctx.from === 'w1'.
function DrillContextBreadcrumb() {
  const ctx = useDrillContext();
  const navigate = useNavigate();

  if (!ctx || ctx.from !== 'w1') return null;

  const summary = ctx.attentionSummary || ctx.focus || null;

  const handleBack = () => {
    clearDrillContext((parsedCtx) => {
      const returnUrl = parsedCtx?.focus
        ? `/?focus=${encodeURIComponent(parsedCtx.focus)}`
        : '/';
      navigate(returnUrl);
    });
  };

  return (
    <div className="agent-drill-breadcrumb" role="navigation" aria-label="Return navigation">
      <button type="button" className="agent-drill-back" onClick={handleBack}>
        ← Back to Command Center
      </button>
      {summary && summary !== 'Command Center' && (
        <span className="agent-drill-summary">· {summary}</span>
      )}
    </div>
  );
}

// ---- QualityStrip (JOBE-0161.2) ----

function QualityStrip({ name }) {
  const overrides = useFlagStore((s) => s.overrides);
  const quality = flaggedFetch('wcc.mock.agentDetail', overrides);

  const tiles = [
    {
      label: 'EVAL PASS RATE',
      value: quality ? `${(quality.evalPassRate.value * 100).toFixed(0)}%` : '—',
      tone: quality ? toneForRate(quality.evalPassRate.value, 0.90, 'above-ok') : undefined,
      spark: quality ? (
        <Spark
          mode="absolute"
          data={quality.evalPassRate.history}
          target={quality.evalPassRate.target}
          ariaLabel="Eval pass rate trend"
        />
      ) : null,
    },
    {
      label: 'TASK THROUGHPUT',
      value: quality ? String(quality.taskThroughput.value) : '—',
      unit: quality ? 'tasks/day' : undefined,
      tone: quality ? toneForDeviation(quality.taskThroughput.history) : undefined,
      spark: quality ? (
        <Spark
          mode="absolute"
          data={quality.taskThroughput.history}
          ariaLabel="Task throughput trend"
        />
      ) : null,
    },
    {
      label: 'P50 LATENCY',
      value: quality ? String(quality.p50LatencyMs.value) : '—',
      unit: quality ? 'ms' : undefined,
      tone: quality ? toneForRate(quality.p50LatencyMs.value, 1000, 'below-ok') : undefined,
      spark: quality ? (
        <Spark
          mode="absolute"
          data={quality.p50LatencyMs.history}
          target={quality.p50LatencyMs.target}
          ariaLabel="p50 latency trend"
        />
      ) : null,
    },
    {
      label: 'ERROR RATE',
      value: quality ? `${(quality.errorRate.value * 100).toFixed(1)}%` : '—',
      tone: quality ? toneForRate(quality.errorRate.value, 0.05, 'below-ok') : undefined,
      spark: quality ? (
        <Spark
          mode="absolute"
          data={quality.errorRate.history}
          target={quality.errorRate.target}
          ariaLabel="Error rate trend"
        />
      ) : null,
    },
  ];

  return (
    <div className="agent-quality-strip">
      {tiles.map((tile) => (
        <div key={tile.label} {...(!quality ? { 'data-testid': 'quality-tile-off' } : {})}>
          <KpiTile
            label={tile.label}
            value={tile.value}
            unit={tile.unit}
            tone={tile.tone}
            spark={tile.spark}
          />
        </div>
      ))}
    </div>
  );
}

// ---- OutcomesTab (JOBE-0161.3) ----

function OutcomesTab({ name }) {
  const overrides = useFlagStore((s) => s.overrides);
  const outcomes = flaggedFetch('agent-detail-outcomes-mock', overrides);

  if (!outcomes || outcomes.length === 0) {
    return (
      <div className="agent-tab-empty">
        <p>No recent task outcomes.</p>
        <p>Task history will appear here once outcomes are recorded.</p>
      </div>
    );
  }

  return (
    <table className="agent-outcomes-table">
      <thead>
        <tr>
          <th>Task</th>
          <th>Status</th>
          <th>Started</th>
          <th>Duration</th>
        </tr>
      </thead>
      <tbody>
        {outcomes.map((task) => {
          const { tone, label } = outcomeStatusTone(task.status);
          return (
            <tr key={task.id}>
              <td>
                {/* TODO(JOBE-task-detail): wire to real task detail route */}
                <Link to={`/tasks/${task.id}`}>{task.title}</Link>
              </td>
              <td>
                <StatusPill tone={tone} label={label} />
              </td>
              <td>{formatTime(task.startedAt)}</td>
              <td>{formatDuration(task.durationMs)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- CapabilitiesTab (JOBE-0161.4) ----

function CapabilitiesTab({ name, agentURN }) {
  const navigate = useNavigate();
  const [capRows, setCapRows] = useState(null);

  useEffect(() => {
    if (!agentURN && !name) return;
    // TODO(JOBE-0161-followup): dedicated /api/agents/{name}/capabilities
    // endpoint would eliminate the fan-out
    fetch('/api/applications')
      .then((r) => r.json())
      .then((apps) => Promise.all(
        (Array.isArray(apps) ? apps : []).map((app) =>
          fetch(`/api/applications/${encodeURIComponent(app.name || app)}/access`)
            .then((r) => r.json())
            .then((view) => ({ app: app.name || app, view }))
            .catch(() => null)
        )
      ))
      .then((results) => {
        const rows = [];
        for (const result of results) {
          if (!result) continue;
          const { app, view } = result;
          const principal = (view.principals || [])
            .find((p) => p.name === agentURN || (name && p.name.endsWith(`:${name}`)));
          if (!principal) continue;
          for (const grant of (principal.grants || [])) {
            if (grant.decision === 'allow') {
              rows.push({
                capabilityID: grant.capabilityID,
                application:  app,
                via:          grant.via || '—',
              });
            }
          }
        }
        setCapRows(rows.length > 0 ? rows : []);
      })
      .catch(() => setCapRows([]));
  }, [agentURN, name]);

  const openApp = (appName) => {
    // TODO(JOBE-0148-landed): switch to modal open when ApplicationDetail supports controlled open prop
    navigate(`/applications/${encodeURIComponent(appName)}`);
  };

  if (capRows === null) {
    return (
      <table className="agent-capabilities-table">
        <thead>
          <tr>
            <th>Capability</th>
            <th>Full URN</th>
            <th>Application</th>
            <th>Sensitivity</th>
            <th>Granted via</th>
          </tr>
        </thead>
        <tbody>
          <tr><td colSpan={5}>Loading…</td></tr>
        </tbody>
      </table>
    );
  }

  if (capRows.length === 0) {
    return (
      <div className="agent-tab-empty">
        <p>This agent has no capability grants.</p>
        <p>Grant access via the Applications tab (Applications → app name → Access).</p>
      </div>
    );
  }

  return (
    <table className="agent-capabilities-table">
      <thead>
        <tr>
          <th>Capability</th>
          <th>Full URN</th>
          <th>Application</th>
          <th>Sensitivity</th>
          <th>Granted via</th>
        </tr>
      </thead>
      <tbody>
        {capRows.map((row, idx) => {
          const parsed = parseCapabilityURN(row.capabilityID);
          return (
            <tr key={`${row.capabilityID}-${idx}`}>
              <td>{parsed.label}</td>
              <td>
                <span className="agent-cap-urn" data-urn={row.capabilityID}>
                  {row.capabilityID}
                </span>
              </td>
              <td>
                <PillChip
                  label={row.application}
                  onClick={() => openApp(row.application)}
                />
              </td>
              <td>
                {/* TODO(JOBE-0161-followup): join with capabilities endpoint for sensitivity field */}
                —
              </td>
              <td>{row.via}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ---- AgentDetail ----

export function AgentDetail() {
  const { name } = useParams();
  const navigate = useNavigate();
  const path = name ? `/api/agents/${encodeURIComponent(name)}` : null;
  const { data: agent, error, loading } = usePolledJSON(path);
  // JOBE-0193: per-route document title — agent display name once
  // loaded, generic 'Agent' label while in-flight.
  useDocumentTitle(agent?.displayName || agent?.name || name || 'Agent');
  const [tab, setTab] = useState('outcomes');

  // agentURN for capability fan-out (JOBE-0161.4)
  const agentURN = agent && safeStr(agent.URN)
    ? stringifyURN(agent.URN)
    : name ? `urn:jobe:agent:${name}` : '';

  // JOBE-0161.5: PromptOps chip carries drill context so W5 can restore agent context (J1 step 5).
  const promptopsHref = withDrillContext(
    `/promptops/agent/${encodeURIComponent(name || '')}`,
    { from: 'w2', agentName: name || '', agentURN },
  );

  // JOBE-0165.1: identity head pulled from the shared IdentityHead export.
  // The component needs a nameSlug for the avatar; passes the URL :name param.
  const identityAgent = agent ? { ...agent, nameSlug: name || '' } : { nameSlug: name || '' };

  return (
    <div className="agent-detail-page" data-agent={name}>
      <DrillContextBreadcrumb />

      <IdentityHead agent={identityAgent}>
        <PillChip
          label="PromptOps"
          onClick={() => navigate(promptopsHref)}
        />
      </IdentityHead>

      <QualityStrip name={name} />

      <div className="agent-detail-tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            role="tab"
            id={`agent-detail-tab-${t}`}
            aria-controls={`agent-detail-panel-${t}`}
            aria-selected={tab === t}
            tabIndex={tab === t ? 0 : -1}
            className={`agent-detail-tab${tab === t ? ' on' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'outcomes' ? 'Outcomes' : t === 'activity' ? 'Activity' : t === 'capabilities' ? 'Capabilities' : t}
          </button>
        ))}
      </div>

      {tab === 'outcomes' && (
        <div
          role="tabpanel"
          id="agent-detail-panel-outcomes"
          aria-labelledby="agent-detail-tab-outcomes"
        >
          <OutcomesTab name={name} />
        </div>
      )}
      {tab === 'activity' && (
        <div
          role="tabpanel"
          id="agent-detail-panel-activity"
          aria-labelledby="agent-detail-tab-activity"
          className="agent-activity-tab"
        >
          <AgentActivityRollup agent={name} />
        </div>
      )}
      {tab === 'capabilities' && (
        <div
          role="tabpanel"
          id="agent-detail-panel-capabilities"
          aria-labelledby="agent-detail-tab-capabilities"
        >
          <CapabilitiesTab name={name} agentURN={agentURN} />
        </div>
      )}

      <AgentDetailBackLink />
    </div>
  );
}

// AgentDetailBackLink shows a context-sensitive back affordance without
// hard-coding a destination. Label reflects the action ("Back"), not a
// specific page, because the referring surface varies (Org page, search
// result, deep-link from chat, etc.).
function AgentDetailBackLink() {
  const navigate = useNavigate();
  // Only show the back affordance when the user arrived via in-app
  // navigation. Direct deep-link entries (history.length === 1 in a
  // fresh tab) have nowhere to go back to; rendering "Back" then would
  // either be a no-op or bounce to about:blank.
  if (typeof window === 'undefined' || window.history.length <= 1) return null;
  return (
    <button
      type="button"
      className="agent-detail-back"
      onClick={() => navigate(-1)}
    >
      ← Back
    </button>
  );
}

// AgentSummaryTab renders the §4 four-block grid.
function AgentSummaryTab({ agent, loading, error }) {
  if (error) {
    return <div className="card"><p className="errorMsg">Failed to load: {error}</p></div>;
  }
  if (loading) {
    return <div className="card"><p>Loading…</p></div>;
  }
  if (!agent) {
    return <div className="card"><p>No data.</p></div>;
  }

  const roleSlug = Array.isArray(agent.Roles) && agent.Roles.length > 0
    ? urnSlug(stringifyURN(agent.Roles[0])) : '';
  const deptSlug = urnSlug(stringifyURN(agent.Department));
  const modelPrimary = agent.Model && agent.Model.Primary;

  return (
    <div className="agent-summary-grid">
      <div className="card agent-summary-block agent-summary-identity">
        <h4>Identity</h4>
        <dl>
          <dt>urn</dt><dd>{agent.URN ? String(agent.URN) : dash()}</dd>
          <dt>name</dt><dd>{safeStr(agent.DisplayName) || urnSlug(stringifyURN(agent.URN)) || dash()}</dd>
          <dt>conversational</dt><dd>{agent.Conversational === true ? 'true' : agent.Conversational === false ? 'false' : dash()}</dd>
        </dl>
      </div>

      <div className="card agent-summary-block agent-summary-runtime">
        <h4>Runtime health</h4>
        <dl>
          <dt>phase</dt><dd>{safeStr(agent.Phase) || dash()}</dd>
          <dt>ready</dt><dd>{agent.Ready === true ? 'true' : agent.Ready === false ? 'false' : dash()}</dd>
          <dt>reason</dt><dd>{safeStr(agent.ReadyReason) || dash()}</dd>
          <dt>message</dt><dd>{safeStr(agent.ReadyMessage) || dash()}</dd>
        </dl>
      </div>

      <div className="card agent-summary-block agent-summary-seat">
        <h4>Org seat</h4>
        <dl>
          <dt>role</dt><dd>{roleSlug || dash()}</dd>
          <dt>department</dt><dd>{deptSlug || dash()}</dd>
          <dt>status</dt><dd>{safeStr(agent.Status) || dash()}</dd>
          <dt>model</dt><dd>{safeStr(modelPrimary) || dash()}</dd>
        </dl>
      </div>

      <div className="card agent-summary-block agent-summary-budget">
        <h4>Budget / usage</h4>
        <dl>
          <dt>tokens today</dt><dd>{dash()}</dd>
          <dt>cost today</dt><dd>{dash()}</dd>
          <dt>invocations/hr</dt><dd>{dash()}</dd>
        </dl>
      </div>

      <div className="card agent-summary-block agent-summary-work">
        <h4>Current work</h4>
        <dl>
          <dt>open tasks</dt><dd>{dash()}</dd>
          <dt>open proposals</dt><dd>{dash()}</dd>
          <dt>resource requests</dt><dd>{dash()}</dd>
        </dl>
      </div>

      <div className="card agent-summary-block agent-summary-schedule">
        <h4>Next schedule</h4>
        <dl>
          <dt>next run</dt><dd>{dash()}</dd>
        </dl>
      </div>
    </div>
  );
}
